import { z } from 'zod';
import { IdSchema } from './encoding.js';
/**
 * Master-data migration contracts (FRD §15; SDD §21; ERD §8.8).
 *
 * A **batch** loads one target record type from one approved source file (CSV). Each source row is
 * an **item**: staged, validated by a dry run through the owning module's create use case,
 * approved through the shared workflow, applied, reconciled to the source's control total and
 * locked. Opening balances and open transactions are not loaded here (FRD-MIG-004, MIG-018).
 */
export const MIGRATION_BATCH_STATUSES = [
    'created',
    'staged',
    'validated',
    'submitted',
    'approved',
    'applying',
    'applied',
    'reconciled',
    'locked',
    'failed',
];
export const MigrationBatchStatusSchema = z.enum(MIGRATION_BATCH_STATUSES);
export const MIGRATION_ITEM_OUTCOMES = ['pending', 'accepted', 'rejected', 'out_of_scope'];
export const MigrationItemOutcomeSchema = z.enum(MIGRATION_ITEM_OUTCOMES);
export const MigrationValidationResultSchema = z.enum(['valid', 'invalid']);
/** One column of an importer's CSV template. */
export const MigrationImporterColumnSchema = z.object({
    name: z.string(),
    required: z.boolean(),
    description: z.string(),
});
/** A target record type a batch can load, with its declared, code-reviewed mapping (SDD §21.2). */
export const MigrationImporterSchema = z.object({
    recordType: z.string(),
    label: z.string(),
    /** The column that identifies a source row across batches (FRD-MIG-023). */
    keyColumn: z.string(),
    columns: z.array(MigrationImporterColumnSchema),
    /** What the load relies on being loaded or configured first. */
    notes: z.string(),
    /** Example rows for the sample CSV (CLR-59): column → value; empty when there is none. */
    sample: z.array(z.record(z.string(), z.string())),
});
/**
 * The status a migrated master record is loaded with (FRD-MIG-009: "active or inactive status").
 * An empty cell loads the record active; `inactive` creates it and then deactivates it through the
 * owning module's own change use case, so the module's status rules apply.
 */
export const MigrationRecordStatusSchema = z.enum(['active', 'inactive']);
/**
 * The opening value an asset row carries besides the register attributes (FRD-AST-015; ERD §7.4
 * Asset "opening accumulated depreciation as migrated"): AED, at most two decimals.
 */
export const MigratedAssetOpeningValuesSchema = z.strictObject({
    openingAccumulatedDepreciation: z
        .string()
        .trim()
        .regex(/^\d{1,17}(\.\d{1,2})?$/, 'must be a decimal amount with at most 2 decimals'),
});
/** Why a row was refused: the owning module's field-level refusal, or a migration rule. */
export const MigrationReasonSchema = z.object({
    field: z.string(),
    rule: z.string(),
    message: z.string(),
});
/** The stored result of a reconciliation (FRD-MIG-024, MIG-025, MIG-027). */
export const MigrationReconciliationSchema = z.object({
    controlRowCount: z.number().int(),
    itemCount: z.number().int(),
    accepted: z.number().int(),
    rejected: z.number().int(),
    outOfScope: z.number().int(),
    pending: z.number().int(),
    /** Rejected rows neither reloaded by a later batch nor accepted as out of scope. */
    unresolvedRejected: z.number().int(),
    /** Control total − (accepted + rejected + out of scope). */
    difference: z.number().int(),
    reconciled: z.boolean(),
    reconciledAt: z.string(),
});
export const MigrationBatchSchema = z.object({
    id: IdSchema,
    batchNumber: z.string(),
    targetRecordType: z.string(),
    targetLabel: z.string(),
    sourceReference: z.string(),
    sourceFileName: z.string().nullable(),
    /** The control total: the number of rows the approved source says it holds. */
    controlRowCount: z.number().int(),
    status: MigrationBatchStatusSchema,
    itemCount: z.number().int(),
    validCount: z.number().int(),
    invalidCount: z.number().int(),
    acceptedCount: z.number().int(),
    rejectedCount: z.number().int(),
    outOfScopeCount: z.number().int(),
    pendingCount: z.number().int(),
    reconciliation: MigrationReconciliationSchema.nullable(),
    failureReason: z.string().nullable(),
    createdAt: z.string(),
    stagedAt: z.string().nullable(),
    validatedAt: z.string().nullable(),
    submittedAt: z.string().nullable(),
    approvedAt: z.string().nullable(),
    appliedAt: z.string().nullable(),
    reconciledAt: z.string().nullable(),
    lockedAt: z.string().nullable(),
    failedAt: z.string().nullable(),
    /** For `If-Match` on every step (SDD §7.3). */
    version: z.number().int(),
});
export const CreateMigrationBatchRequestSchema = z.strictObject({
    targetRecordType: z.string().min(1).max(100),
    sourceReference: z.string().trim().min(1).max(500),
    controlRowCount: z.number().int().min(0).max(1_000_000),
});
export const MigrationBatchItemSchema = z.object({
    id: IdSchema,
    batchId: IdSchema,
    batchNumber: z.string(),
    targetRecordType: z.string(),
    sourceRowNumber: z.number().int(),
    sourceRowKey: z.string().nullable(),
    /** The staged row, column → value; designated fields are masked (FRD-MIG-006). */
    payload: z.record(z.string(), z.string().nullable()),
    validationResult: MigrationValidationResultSchema.nullable(),
    validationReasons: z.array(MigrationReasonSchema),
    outcome: MigrationItemOutcomeSchema,
    rejectionReasons: z.array(MigrationReasonSchema),
    /** The record the row produced (FRD-MIG-033); its type is `targetRecordType`. */
    resultRecordId: IdSchema.nullable(),
    outOfScopeReason: z.string().nullable(),
    outOfScopeAt: z.string().nullable(),
    version: z.number().int(),
});
/** Accepting a rejected row as out of scope needs a stated reason (FRD-MIG-025). */
export const MarkMigrationItemOutOfScopeRequestSchema = z.strictObject({
    reason: z.string().trim().min(1).max(500),
});
//# sourceMappingURL=migration.js.map